import { useEffect, useContext } from "react";
import { Group, Button, useMantineTheme } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import {
  CreateEmployeeStationInput,
  useCreateEmployeeStationMutation,
  useGetEmployeeStationLazyQuery,
  useUpdateEmployeeStationMutation,
} from "src/generated/graphql";
import { number, object, string } from "yup";
import { useModals } from "@mantine/modals";
import TextInput from "src/components/TextInput/TextInput";
import Textarea from "src/components/Textarea/Textarea";
import NumberInput from "src/components/NumberInput/NumberInput";
import FormCard from "src/components/FormCard";
import { MESSAGES } from "src/utils/constants";
import { AppContext } from "src/api/AppContext";

type IFormProps = Omit<CreateEmployeeStationInput, "location">;

interface IStationProps {
  isEdit: boolean;
  employeeStationId: string;
  onRefetch: () => void;
  isAllowWrite: boolean;
}

const stationFormSchema = object().shape({
  name: string()
    .trim("No spaces allowed")
    .strict(true)
    .min(2, "Name should must be least 2 letters")
    .max(30, "Name must not be greater than 30 letters")
    .required("Name is required"),
  payRate: number().min(0.1).required("Payrate is required"),
});

function CreateStation({
  isEdit = false,
  employeeStationId,
  onRefetch,
  isAllowWrite,
}: IStationProps) {
  const modals = useModals();
  const theme = useMantineTheme();
  const { currentLocation } = useContext(AppContext);

  const [isFormActive] = useDisclosure(!isAllowWrite);

  const [getEmployeeStation, { data }] = useGetEmployeeStationLazyQuery({
    onCompleted: (data) => {
      if (data && data.employeeStationOne) {
        const { name, description, payRate, minShift, maxShift } =
          data.employeeStationOne;
        let _data = {
          name,
          description,
          payRate,
          minShift,
          maxShift,
        };
        form.setValues(_data);
      }
    },
  });
  const [createStation, {}] = useCreateEmployeeStationMutation({
    onCompleted: () => {
      onRefetch();
      modals.closeAll();
    },
  });

  const [updateStation, {}] = useUpdateEmployeeStationMutation({
    onCompleted: () => {
      onRefetch();
      modals.closeAll();
    },
  });

  useEffect(() => {
    if (isEdit && employeeStationId) {
      getEmployeeStation({ variables: { id: employeeStationId } });
    }
  }, [employeeStationId]);

  const form = useForm<IFormProps>({
    initialValues: {
      name: "",
      description: "",
      payRate: Number(),
      minShift: Number(),
      maxShift: Number(),
      employee: [],
    },
    validate: yupResolver(stationFormSchema),
  });
  const handleSubmit = async (values: IFormProps) => {
    if (!isEdit) {
      await createStation({
        variables: {
          data: {
            ...values,
            location: {
              id: currentLocation?.id!,
            },
            employee: [],
          },
        },
      });
    } else {
      await updateStation({
        variables: {
          id: employeeStationId,
          data: {
            ...values,
          },
        },
      });
    }
  };

  const isFormUpdated = form.isDirty() && form.isTouched();

  return (
    <FormCard
      title={""}
      hideCard={true}
      loading={false}
      bannerContent={isFormUpdated ? MESSAGES.UNSAVED_CHANGES : ""}
      onSave={form.onSubmit((values) => {
        handleSubmit(values);
      })}
      isFormActive={isFormActive}
    >
      <TextInput
        withAsterisk
        label="Name"
        disabled={isFormActive}
        {...form.getInputProps("name")}
      />

      <Textarea
        label="Description"
        disabled={isFormActive}
        {...form.getInputProps("description")}
      />

      <NumberInput
        withAsterisk
        label="Pay Rate"
        precision={2}
        disabled={isFormActive}
        {...form.getInputProps("payRate")}
      />

      <NumberInput
        label="Minimum Shift Duration"
        disabled={isFormActive}
        {...form.getInputProps("minShift")}
      />
      <NumberInput
        label="Maximum Shift Duration"
        disabled={isFormActive}
        {...form.getInputProps("maxShift")}
      />

      <Group position="apart" mt="md" style={{ marginTop: 30 }}>
        <Button
          radius="md"
          variant="outline"
          onClick={() => modals.closeAll()}
          style={{
            flex: 1,
            color: theme.colors.secondaryTextColor[6],
          }}
          sx={{
            backgroundColor: theme.colors.secondary[6],
            "&:hover": {
              background: theme.colors.secondary[5],
            },
          }}
        >
          Cancel
        </Button>
        {isAllowWrite && (
          <Button
            radius="md"
            type="submit"
            disabled={false}
            style={{
              flex: 1,
            }}
            sx={{
              color: theme.colors.primaryTextColor[6],
              "&:hover": {
                backgroundColor: theme.colors.primary[4],
              },
            }}
            loading={false}
          >
            {isEdit ? "Save" : "Create"}
          </Button>
        )}
      </Group>
    </FormCard>
  );
}

export default CreateStation;
